/* External */
import { memo } from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import Card4X3 from '@dtvgo/card-4x3';
import Icon from '@dtvgo/icon';

/* Utils */
import { CardWidth, CardType } from '../../utils/cardsUtils';
import { ImageRatio } from '../../utils/constants';
import { getIconFromBlockStatus } from '../../utils/iconBlockUtils';
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import { Content } from '../../storage/models';
import useLockIcon from '../../hooks/useLockIcon';
import parentalControlBlock from '../../utils/parentalControlUtils';

import { useSubtitleContinueWatching } from './utils';
import { useTitle } from '../../utils/metaDataUtils';

/* Hooks */
import useImageCard from '../../hooks/useImageCard';

/* Styles */
import './CardContinueWatching.scss';

const IMG_WIDTH = CardWidth[CardType.CONTINUE_WATCHING] + 25;
const IMG_DIMENSION = ImageRatio.FOUR_BY_THREE;

function CardContinueWatching({
  className = '',
  focused,
  item: content,
  onMouseDown,
  onMouseEnter,
}) {
  const {
    images,
    title: contentTitle,
    progress,
    duration = 1,
  } = content || {};

  const showLockIcon = useLockIcon(content);
  const isContentBlockedByParentalControl = parentalControlBlock(content);

  const iconName = getIconFromBlockStatus(showLockIcon, isContentBlockedByParentalControl);

  const title = useTitle(content);
  const subtitle = useSubtitleContinueWatching(content);

  const { imageUrl } = useImageCard({
    images,
    imageSize: IMG_WIDTH,
    aspectRatio: IMG_DIMENSION,
  });

  return (
    <Card4X3
      className={
        `dtv-html5-c-card-continue-watching ${
          focused ? 'dtv-html5-c-card-continue-watching--focused' : ''
        } ${className}`
      }
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
    >
      <Card4X3.Image
        className="dtv-html5-c-card-continue-watching__image"
        imageUrl={imageUrl}
        imageAlt={contentTitle || ''}
      >
        {showLockIcon && <Card4X3.Lock className="dtv-html5-c-card-continue-watching__lock" />}
        {(isContentBlockedByParentalControl && !showLockIcon) && (
          <div className="dtv-html5-c-card-continue-watching__parental-control-block">
            <Icon name="parental-block" className="dtv-html5-c-card-continue-watching__parental-control-icon" />
          </div>
        )}
        <Card4X3.Image.Icon
          name={iconName}
          className={`dtv-html5-c-card-continue-watching__image-icon ${showLockIcon ? 'dtv-html5-c-card-continue-watching__image-icon--lock' : ''}
            ${(isContentBlockedByParentalControl && !showLockIcon) ? 'dtv-html5-c-card-continue-watching__image-icon--block-content' : ''}`}
        />
        <Card4X3.Image.Progress
          className="dtv-html5-c-card-continue-watching__progress"
          progress={progress}
          duration={duration}
        />
      </Card4X3.Image>
      <div className="dtv-html5-c-card-continue-watching__metadata">
        <Card4X3.Title className={`dtv-html5-c-card-continue-watching__title ${focused ? 'dtv-html5-c-card-continue-watching__title--focused' : ''}`}>
          {title || ''}
        </Card4X3.Title>
        <Card4X3.SubTitle className="dtv-html5-c-card-continue-watching__subtitle">
          {subtitle}
        </Card4X3.SubTitle>
      </div>
    </Card4X3>
  );
}

CardContinueWatching.propTypes = {
  className: PropTypes.string,
  focused: PropTypes.bool.isRequired,
  item: PropTypes.instanceOf(Content).isRequired,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
};

export default memo(withInteractiveFocusable()(memo(CardContinueWatching)));
